'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the header's own nav link colours and size (DropdownMenu) */

import classNames from 'classnames';
import { MouseEvent, SyntheticEvent } from 'react';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { NAVIGATION_TRANSLATIONS } from '../i18n';

/** Where the toggle leads: what the app works out from the page's path and the brand's locales. */
export type LanguageToggleTarget = {
  /** The same page in the other locale: `/zh/credit-card/all`. Not checked: a missing one is a 404. */
  href: string;
  /** Language tag of the other locale: `zh-hk`. */
  hrefLang: string;
};

export type LanguageToggleProps = LanguageToggleTarget & {
  pushDataLayer: PushDataLayerFn;
  /** The toggle's place among the header's top-level items, in their `index` format: `7.0.0`. */
  index: string;
};

/**
 * EN/ZH switch in the header (CMS-2008): a plain link to the same page in the other locale. Nothing
 * is remembered — no stored preference.
 */
export function LanguageToggle({
  href,
  hrefLang,
  pushDataLayer,
  index,
}: LanguageToggleProps) {
  const { lang } = useCmsClientContext();
  const staticText = NAVIGATION_TRANSLATIONS.get(lang);

  // The query string and anchor are the visitor's, not the rendered page's, so they join the link
  // as it is followed: pointerdown is first for any button and for a long press (before "open in
  // new tab" / "copy link"), click covers the keyboard, contextmenu a menu opened from the keyboard.
  const carryUrlState = (event: SyntheticEvent<HTMLAnchorElement>) => {
    event.currentTarget.href = `${href}${window.location.search}${window.location.hash}`;
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    carryUrlState(event);
    // Fields as GA4 spec #2678 sets them (CMS-2050).
    pushDataLayer({
      event: 'navigate',
      component_type: 'top-nav',
      component_title: `Level 1/Language Toggle_${staticText.languageToggleLabel}`,
      component_index: '',
      action: 'click',
      index,
    });
  };

  return (
    <a
      href={href}
      hrefLang={hrefLang}
      onPointerDown={carryUrlState}
      onClick={handleClick}
      onContextMenu={carryUrlState}
      className={classNames(
        'flex shrink-0 items-center gap-1 whitespace-nowrap font-bold text-[#687076] text-[16px] hover:text-[#212529]',
      )}
      data-component="language-toggle"
      data-header-item="language-toggle"
    >
      <MaterialIcon icon="language" px={20} />
      {/* The visible name is in the other language; the hidden hint is in the page's own, so the
          accessible name carries both (WCAG 2.5.3 label in name). */}
      <span lang={hrefLang}>{staticText.languageToggleLabel}</span>{' '}
      <span className="sr-only">{staticText.languageToggleHint}</span>
    </a>
  );
}
